import dayjs from 'dayjs';
import customParseFormat from 'dayjs/plugin/customParseFormat';

// Extend dayjs with required plugins
dayjs.extend(customParseFormat);

/**
 * Converts a date from YYYY-MM-DD format to DD/MM/YYYY format for display
 * @param {string} dateString - Date in YYYY-MM-DD format
 * @returns {string} - Date in DD/MM/YYYY format or empty string if invalid
 */
export const formatDateForDisplay = (dateString) => {
  if (!dateString) return '';
  
  try {
    const date = dayjs(dateString);
    if (!date.isValid()) return '';
    return date.format('DD/MM/YYYY');
  } catch (error) {
    console.error('Error formatting date for display:', error);
    return '';
  }
};

/**
 * Converts a date from DD/MM/YYYY format to YYYY-MM-DD format for API
 * @param {string} dateString - Date in DD/MM/YYYY format
 * @returns {string} - Date in YYYY-MM-DD format or empty string if invalid
 */
export const formatDateForAPI = (dateString) => {
  if (!dateString) return '';
  
  try {
    // Parse DD/MM/YYYY format
    const date = dayjs(dateString, 'DD/MM/YYYY', true);
    if (!date.isValid()) return '';
    return date.format('YYYY-MM-DD');
  } catch (error) {
    console.error('Error formatting date for API:', error);
    return '';
  }
};

/**
 * Converts a dayjs date object to YYYY-MM-DD format for API
 * @param {dayjs.Dayjs} dayjsDate - dayjs date object
 * @returns {string} - Date in YYYY-MM-DD format or empty string if invalid
 */
export const formatDayjsForAPI = (dayjsDate) => {
  if (!dayjsDate || !dayjsDate.isValid()) return '';
  return dayjsDate.format('YYYY-MM-DD');
};

/**
 * Converts a YYYY-MM-DD date string to a dayjs object
 * @param {string} dateString - Date in YYYY-MM-DD format
 * @returns {dayjs.Dayjs|null} - dayjs object or null if invalid
 */
export const parseDateFromAPI = (dateString) => {
  if (!dateString) return null;
  
  try {
    const date = dayjs(dateString);
    return date.isValid() ? date : null;
  } catch (error) {
    console.error('Error parsing date from API:', error);
    return null;
  }
};

/**
 * Validates if a date string is in DD/MM/YYYY format
 * @param {string} dateString - Date string to validate
 * @returns {boolean} - True if valid DD/MM/YYYY format
 */
export const isValidDisplayDate = (dateString) => {
  if (!dateString) return false;
  
  try {
    const date = dayjs(dateString, 'DD/MM/YYYY', true);
    return date.isValid();
  } catch (error) {
    return false;
  }
};

/**
 * Gets current date in DD/MM/YYYY format
 * @returns {string} - Current date in DD/MM/YYYY format
 */
export const getCurrentDateDisplay = () => {
  return dayjs().format('DD/MM/YYYY');
};

/**
 * Gets current date in YYYY-MM-DD format
 * @returns {string} - Current date in YYYY-MM-DD format
 */
export const getCurrentDateAPI = () => {
  return dayjs().format('YYYY-MM-DD');
};

/**
 * Formats a JavaScript Date object or date string to DD/MM/YYYY format
 * This is a universal formatter that can handle various input types
 * @param {Date|string} dateInput - Date object or date string
 * @returns {string} - Date in DD/MM/YYYY format or empty string if invalid
 */
export const formatDateDDMMYYYY = (dateInput) => {
  if (!dateInput) return '';
  
  try {
    let date;
    if (dateInput instanceof Date) {
      date = dayjs(dateInput);
    } else {
      date = dayjs(dateInput);
    }
    
    if (!date.isValid()) return '';
    return date.format('DD/MM/YYYY');
  } catch (error) {
    console.error('Error formatting date to DD/MM/YYYY:', error);
    return '';
  }
};

/**
 * Formats a JavaScript Date object or date string to DD/MM/YYYY HH:mm format
 * @param {Date|string} dateInput - Date object or date string
 * @returns {string} - Date and time in DD/MM/YYYY HH:mm format or empty string if invalid
 */
export const formatDateTimeDDMMYYYY = (dateInput) => {
  if (!dateInput) return '';
  
  try {
    let date;
    if (dateInput instanceof Date) {
      date = dayjs(dateInput);
    } else {
      date = dayjs(dateInput);
    }
    
    if (!date.isValid()) return '';
    return date.format('DD/MM/YYYY HH:mm');
  } catch (error) {
    console.error('Error formatting datetime to DD/MM/YYYY HH:mm:', error);
    return '';
  }
};

/**
 * Replaces browser's toLocaleDateString with DD/MM/YYYY format
 * This can be used as a drop-in replacement for new Date().toLocaleDateString()
 * @param {Date} date - JavaScript Date object
 * @returns {string} - Date in DD/MM/YYYY format
 */
export const toLocaleDateStringDDMMYYYY = (date) => {
  return formatDateDDMMYYYY(date);
};
